import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Music, RefreshCw, ListMusic } from 'lucide-react';
import { useSpotify } from '../SpotifyContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const SpotifyQueue = () => {
  const { accessToken, isConnected } = useSpotify();
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchQueue = async () => {
    if (!accessToken || !isConnected) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/spotify/queue`, {
        params: { access_token: accessToken }
      });
      setQueue(response.data);
    } catch (error) {
      console.error('Error fetching queue:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchQueue();
      // Refresh every 10 seconds
      const interval = setInterval(fetchQueue, 10000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: fetchQueue
  }, [isConnected, accessToken]);

  if (!isConnected) return null;

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(0);
    return `${minutes}:${seconds.padStart(2, '0')}`;
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <ListMusic className="w-5 h-5 text-green-400" />
            Cola de Spotify
          </CardTitle>
          <Button
            onClick={fetchQueue}
            disabled={loading}
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Currently Playing */}
        {queue?.currently_playing && (
          <div className="p-3 bg-green-900/20 border border-green-700/30 rounded">
            <div className="flex items-center gap-2 mb-2">
              <Music className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-xs font-medium">Sonando ahora</span>
            </div>
            <div className="flex gap-3">
              {queue.currently_playing.album.images[0] && (
                <img
                  src={queue.currently_playing.album.images[0].url}
                  alt="Album"
                  className="w-12 h-12 rounded"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {queue.currently_playing.name}
                </p>
                <p className="text-slate-400 text-xs truncate">
                  {queue.currently_playing.artists.join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Queue */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-xs font-medium">
              Próximas {queue?.queue?.length || 0}
              {queue?.total > 20 && ` de ${queue.total}`}
            </span>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {queue?.queue?.length === 0 ? (
              <p className="text-slate-500 text-xs text-center py-4">
                No hay canciones en cola
              </p>
            ) : (
              queue?.queue?.map((track, index) => (
                <div
                  key={`${track.uri}-${index}`}
                  className="flex gap-2 p-2 bg-slate-900/50 rounded hover:bg-slate-900 transition-colors"
                >
                  <span className="text-slate-500 text-xs font-medium w-5 flex-shrink-0 mt-1">
                    {index + 1}
                  </span>
                  {track.album.images[0] && (
                    <img
                      src={track.album.images[0].url}
                      alt="Album"
                      className="w-10 h-10 rounded flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">
                      {track.name}
                    </p>
                    <p className="text-slate-400 text-xs truncate">
                      {track.artists.join(', ')}
                    </p>
                  </div>
                  <span className="text-slate-500 text-xs flex-shrink-0 mt-1">
                    {formatDuration(track.duration_ms)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {queue?.total > 20 && (
          <p className="text-xs text-slate-500 text-center">
            Mostrando primeras 20 canciones
          </p>
        )}
      </CardContent>
    </Card>
  );
};
